/*eslint-disable no-unused-vars */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { frameColumnsForAdminDocumentList } from '../../../../utils/common';
import { axiosInstance } from '../../../../../api/axios';
import APIEndpoints from '../../../../api/APIEndpoints';
import debounce from 'lodash.debounce';
import { HttpSuccessCodes } from '../../../../errorhandling/HttpErrorCodes';
import MasterPermissions from '../../../../api/MasterPermissions';
//Third party packages
import useDate from '../../../../utils/useDate';

const useDocuments = ({
  selectedDocTypeData,
  selectedLocationData,
  docType,
  setDocType,
  physicianType,
}) => {
  const documentsFiltersData = [
    'ALL',
    'BROKER SPECIFIC DOCUMENTS',
    'PROVIDER DOCUMENTS',
    'BLOCK OF BUSINESS SPECIFIC DOCUMENT',
  ];

  const [isActiveFilter, setIsActiveFilter] = useState(documentsFiltersData[0]);
  const isComingFromDocuments = true;
  //The below one is Master List
  const [getDocumentData, setGetDocumentData] = useState();
  //FilteredList
  const [filteredRecords, setFilteredRecords] = useState([]);
  //pagination specific states
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  //search
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [skeletonLoader, setSkeletonLoader] = useState(false);
  const [documentListNoDataError, setDocumentListNoDataError] = useState(false);
  const dispatch = useDispatch();
  const displayPaginationMininumCount = 10;
  const { formatDate } = useDate();
  const { userData } = useSelector((state) => state.user);
  const { UserType } = userData;
  const pageSizeHandler = (pageSizeInfo) => {
    setPageSize(pageSizeInfo.value);
    setCurrentPage(1);
    filterSearchRecords();
  };

  const paginationHandler = (page) => {
    setCurrentPage(page);
    if (page > 1) {
      setFilteredRecords(
        filteredRecords.slice((page - 1) * 10, filteredRecords.length),
      );
    } else {
      filterSearchRecords();
    }
  };

  const debouncedCall = useCallback(
    debounce(
      (currentVal) => (setSearchText(currentVal), setSearch(currentVal)),
      800,
    ),
    [],
  );

  const onSearch = (event) => {
    setCurrentPage(1);
    setSearchText(event.target.value);
    debouncedCall(event.target.value);
  };

  const columnNames = useMemo(() => {
    return frameColumnsForAdminDocumentList();
  }, [getDocumentData]);

  const getTypeField = (type) => {
    return type === 'BROKER' || type === 'Broker Documents'
      ? 'Broker Specific Documents'
      : type === 'CLIENT'
      ? 'Employer Specific Document'
      : type === 'Block of Business' ||
        type === 'Block of Business specific Document'
      ? 'Block of Business Specific Document'
      : type === 'Location'
      ? 'Location Specific Document'
      : type === 'PHYSICIAN' || type === 'CONNECTOR' || type === 'FACILITY'
      ? 'Provider Documents'
      : '';
  };

  const frameDataToDisplay = (docData) => {
    if (docData?.length > 0) {
      const records = [];
      const iterationCount = docData.length;
      for (let iIndex = 0; iIndex < iterationCount; iIndex++) {
        records.push({
          Title: docData[iIndex].Title,
          Type: docType?.label,
          Sentby: docData[iIndex].SentBy || '',
          SentDate: docData[iIndex].SentDate,
          documents: `${docData[iIndex].TotalDocument} Document${
            docData[iIndex].TotalDocument > 1 ? 's' : ''
          }`,
          Comment: docData[iIndex].Comment,
          senddocumentfor: `${userData.FirstName} ${userData.LastName}`,
          TypeCategory: docData[iIndex].Category || '',
          ReferenceNumber: docData[iIndex].ReferenceNumber,
          Source: docData[iIndex].Source,
          Oid: docData[iIndex].Oid || '',
          DocumentControlNumber: docData[iIndex].DocumentControlNumber || '',
          FileName: docData[iIndex].FileName || '',
          LocationOid: docData[iIndex].LocationOid || '',
          //oid: docData[iIndex].Oid,
          documentType: docData[iIndex].EntityType,
        });
      }
      setFilteredRecords(records);
    } else {
      setFilteredRecords([]);
    }
  };

  const getEntityParams = () => {
    let entityParams;
    switch (docType.value) {
      case 'Block of Business':
        entityParams = `/EntityType/${docType.value}/EntityKey/${selectedDocTypeData.BlkOfBusId}`;
        break;
      case 'CLIENT':
        entityParams = `/EntityType/${docType.value}/EntityKey/${selectedDocTypeData.BlkOfBusId},${selectedDocTypeData.ClientId}`;
        break;
      case 'Location':
        entityParams = `/EntityType/${docType.value}/EntityKey/${selectedDocTypeData.BlkOfBusId},${selectedDocTypeData.ClientId},${selectedLocationData.LocationName}`;
        break;
      case 'BROKER':
        entityParams = `/EntityType/${docType.value}/EntityKey/${selectedDocTypeData.Code}`;
        break;
      case 'PHYSICIAN':
      case 'CONNECTOR':
      case 'FACILITY':
        entityParams = `/EntityType/${
          physicianType === 'D' ? 'PHYSICIAN' : 'FACILITY'
        }/EntityKey/${
          physicianType === 'D'
            ? selectedDocTypeData.PhysicianID
            : selectedDocTypeData.FacilityID
        }`;
        break;
    }
    return entityParams;
  };

  const getDocumentList = async (checkDefaultValue = false) => {
    setDocumentListNoDataError(false);
    try {
      const DocumentList = await axiosInstance.get(
        `${APIEndpoints.getDocumentUploadList()}${getEntityParams()}${
          searchText !== '' ? `/searchkey/${searchText}` : ''
        }`,
      );
      if (DocumentList.status === HttpSuccessCodes.HTTP_SUCCESS_CODE_200) {
        const data = DocumentList?.data?.Response?.listOfDocumentUploadObj;
        const InternalPermission = MasterPermissions.CheckPermission(
          'View Internal Document',
        );
        const ExpternalPermission = MasterPermissions.CheckPermission(
          'View External Document',
        );
        const permissionFilteredData =
          InternalPermission && ExpternalPermission
            ? data
            : InternalPermission
            ? data.filter((item) => item.Source === 'JMP')
            : ExpternalPermission
            ? data.filter((item) => item.Source === 'JAVELINA')
            : [];

        //setGetDocumentData(
        //DocumentList?.data?.Response?.listOfDocumentUploadObj,
        //);
        setGetDocumentData(permissionFilteredData);
        if (permissionFilteredData?.length === 0) {
          setDocumentListNoDataError(true);
        }
        setSkeletonLoader(false);
      }
    } catch (error) {
      setGetDocumentData([]);
      setFilteredRecords([]);
      setSkeletonLoader(false);
      if (error?.toString()?.includes('404')) {
        setDocumentListNoDataError(true);
      }
    }
  };
  const selectDocType = (reset) => {
    if (docType === undefined || reset) {
      if (
        UserType === 'Broker' &&
        MasterPermissions.CheckPermission('Upload for self')
      ) {
        setDocType({
          label: 'Broker Specific Document',
          value: 'BROKER',
        });
      } else if (
        UserType === 'Insurer' &&
        MasterPermissions.CheckPermission('Upload for BLOB')
      ) {
        setDocType({
          label: 'Block of Business specific Document',
          value: 'Block of Business',
        });
      } else if (
        UserType === 'Client' &&
        MasterPermissions.CheckPermission('Upload for Client')
      ) {
        setDocType({
          label: 'Employer Specific Document',
          value: 'CLIENT',
        });
      } else if (
        UserType === 'Provider' &&
        MasterPermissions.CheckPermission('Upload for Physician/Facility')
      ) {
        setDocType({
          label: 'Provider Document',
          value: 'PHYSICIAN',
        });
      }
    }
    if (reset) {
      setSearch('');
      setSearchText('');
    }
  };

  const sortingDataHandler = (data) => {
    if (data?.length > 0) {
      const sortByTime = data?.slice()?.sort((a, b) => {
        return b?.date - a?.date;
      });
      const sortByPin = sortByTime
        ?.filter((p1) => p1.pin === true)
        .concat(sortByTime?.filter((p1) => p1.pin !== true));
      return sortByPin;
    } else {
      return [];
    }
  };

  const filterSearchRecords = () => {
    const requestTitleFilteredData = getDocumentData?.filter((item) =>
      item?.Title?.toLowerCase()?.includes(searchText.toLowerCase()),
    );

    const requestDateFilteredData = getDocumentData?.filter((item) =>
      item?.SentDate?.toLowerCase()?.includes(searchText.toLowerCase()),
    );

    const finalFilteredData = new Set(
      sortingDataHandler([
        ...requestTitleFilteredData,
        ...requestDateFilteredData,
      ]),
    );
    //setPageSize(10);
    setCurrentPage(1);
    setTotalRecords([...finalFilteredData].length);
    frameDataToDisplay([...finalFilteredData]);
  };

  useEffect(() => {
    if (getDocumentData?.length > 0) {
      frameDataToDisplay(getDocumentData);
      setTotalRecords(getDocumentData.length);
      setGetDocumentData(getDocumentData);
    }
  }, [getDocumentData]);

  useEffect(() => {
    console.log('selectedLocationData--@', selectedLocationData);
    console.log('docType--@', docType);
    console.log('physicianType--@', physicianType);
    console.log('selectedDocTypeData--@', selectedDocTypeData);

    selectDocType(false);
    setSkeletonLoader(true);
    getDocumentList();
  }, [selectedDocTypeData, selectedLocationData]);

  useEffect(() => {
    getDocumentData && getDocumentData?.length > 0 && filterSearchRecords();
  }, [search]);

  return {
    filteredRecords,
    pageSize,
    currentPage,
    totalRecords,
    searchText,
    columnNames,
    displayPaginationMininumCount,
    setFilteredRecords,
    setPageSize,
    setCurrentPage,
    setTotalRecords,
    setSearchText,
    pageSizeHandler,
    paginationHandler,
    onSearch,
    isActiveFilter,
    setIsActiveFilter,
    isComingFromDocuments,
    documentsFiltersData,
    skeletonLoader,
    selectDocType,
    documentListNoDataError,
  };
};

export default useDocuments;
